import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import TopBar from './TopBar'

/**
 * Vista "I miei progressi" dell'atleta: elenco degli esercizi monitorati e,
 * quando se ne apre uno, il dettaglio con grafico dell'andamento e storico.
 *
 * Componente di sola presentazione: non legge né scrive sul database, riceve
 * tutto da HomeAtleta.js. Estratto da lì per ridurne la dimensione: il
 * comportamento è identico a prima.
 */

/** Una data 'YYYY-MM-DD' letta come giorno locale, non come mezzanotte UTC.
 *  new Date('2026-10-20') vale mezzanotte UTC: a ovest di Greenwich diventa il
 *  19, e l'atleta vede i propri allenamenti spostati di un giorno. Il resto
 *  dell'app aggiunge già 'T00:00:00' per questo motivo; qui era rimasto fuori. */
function giorno(data) {
  return new Date(String(data).length === 10 ? `${data}T00:00:00` : data)
}

export default function ProgressiAtleta({
  progressiExSel,          // esercizio aperto in dettaglio (null = elenco)
  setProgressiExSel,
  progressiStorico,        // record di tracking dell'esercizio aperto
  datiGrafico,             // punti già pronti per il grafico
  tracking,                // tutti i record dell'atleta (per l'elenco)
  setVistaProgressi,
  setMostraNotifiche,
  setMostraLibreria,
  setLogData,
  apriEsercizioProgressi,  // apre il dettaglio di un esercizio
  onCambiaTab,
}) {
  return (
  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }} className="fade-in">
    {/* Una sola freccia, che risale di un livello alla volta (come il tasto indietro):
        dal dettaglio torna alla lista, dalla lista torna alla Home.
        Prima c'erano due frecce: quella interna tornava alla lista, questa invece dal
        dettaglio usciva dai Progressi lasciando il tab su "progressiatleta" — e la
        card Progressi in home smetteva di rispondere. */}
    <TopBar
      onIndietro={() => {
        if (progressiExSel) { setProgressiExSel(null); setLogData({}); return }
        setVistaProgressi(false); setLogData({}); if (onCambiaTab) onCambiaTab('home')
      }}
      onNotifiche={() => setMostraNotifiche(true)}
    />
    <div className="scroll-area fade-in">
      <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 20, marginBottom: 4 }}>
        {progressiExSel ? progressiExSel.nome : 'I miei progressi'}
      </div>
      {progressiExSel ? (
        /* ── VISTA DETTAGLIO ESERCIZIO ── */
        <>
          {/* Record + livello */}
          {(() => {
            const records = progressiStorico
            const maxRip = Math.max(0, ...records.filter(r => r.ripetizioni).map(r => r.ripetizioni))
            const maxTempo = Math.max(0, ...records.filter(r => r.durata_secondi).map(r => r.durata_secondi))
            const maxPeso = Math.max(0, ...records.filter(r => r.peso > 0).map(r => r.peso))
            // Giorni distinti, non righe: ogni serie è una riga sua, quindi
            // contare le righe faceva leggere "3 sessioni" a chi aveva fatto
            // tre serie in un pomeriggio solo.
            const totaleSessioni = new Set(records.map(r => r.data)).size
            const totaleSerie = records.length

            // Calcola livello basato su ripetizioni o tempo
            let livello = null, livelloLabel = null, livelloColore = 'var(--muted)'
            if (progressiExSel.tipo_misura !== 'tempo' && maxRip > 0) {
              if (maxRip >= 30) { livello = 5; livelloLabel = '🔥 Avanzato+'; livelloColore = 'var(--accent3)' }
              else if (maxRip >= 20) { livello = 4; livelloLabel = '💪 Avanzato'; livelloColore = 'var(--accent)' }
              else if (maxRip >= 12) { livello = 3; livelloLabel = '📈 Intermedio'; livelloColore = 'var(--accent2)' }
              else if (maxRip >= 6) { livello = 2; livelloLabel = '🌱 Base'; livelloColore = 'var(--green)' }
              else { livello = 1; livelloLabel = '🎯 Principiante'; livelloColore = 'var(--purple)' }
            } else if (progressiExSel.tipo_misura === 'tempo' && maxTempo > 0) {
              if (maxTempo >= 60) { livello = 5; livelloLabel = '🔥 Avanzato+'; livelloColore = 'var(--accent3)' }
              else if (maxTempo >= 45) { livello = 4; livelloLabel = '💪 Avanzato'; livelloColore = 'var(--accent)' }
              else if (maxTempo >= 30) { livello = 3; livelloLabel = '📈 Intermedio'; livelloColore = 'var(--accent2)' }
              else if (maxTempo >= 15) { livello = 2; livelloLabel = '🌱 Base'; livelloColore = 'var(--green)' }
              else { livello = 1; livelloLabel = '🎯 Principiante'; livelloColore = 'var(--purple)' }
            }

            return (
              <div className="card">
                <div className="card-body">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: livello ? 14 : 0 }}>
                    {maxRip > 0 && (
                      <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
                        <div style={{ fontFamily: '"Instrument Sans", sans-serif', fontWeight: 600, fontSize: 28, color: 'var(--accent)', lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>{maxRip}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>rip massimo</div>
                      </div>
                    )}
                    {maxTempo > 0 && (
                      <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
                        <div style={{ fontFamily: '"Instrument Sans", sans-serif', fontWeight: 600, fontSize: 28, color: 'var(--accent2)', lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>{maxTempo}s</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>tempo massimo</div>
                      </div>
                    )}
                    {maxPeso > 0 && (
                      <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
                        <div style={{ fontFamily: '"Instrument Sans", sans-serif', fontWeight: 600, fontSize: 28, color: 'var(--accent3)', lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>+{maxPeso}kg</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>carico massimo</div>
                      </div>
                    )}
                    <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontFamily: '"Instrument Sans", sans-serif', fontWeight: 600, fontSize: 28, color: 'var(--muted)', lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>{totaleSessioni}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
                        {totaleSessioni === 1 ? 'sessione' : 'sessioni'}
                        {totaleSerie > totaleSessioni && ` · ${totaleSerie} serie`}
                      </div>
                    </div>
                  </div>
                  {livello && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 3, marginBottom: 4 }}>
                          {[1,2,3,4,5].map(n => (
                            <div key={n} style={{ flex: 1, height: 6, borderRadius: 3,
                              background: n <= livello ? livelloColore : 'var(--surface2)' }} />
                          ))}
                        </div>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: livelloColore, flexShrink: 0 }}>{livelloLabel}</span>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

          {/* Grafico */}
          {datiGrafico.length > 0 && (
            <div className="card">
              <div className="card-header"><div className="card-title">📈 Progressi nel tempo</div></div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {(() => {
                  const hasRip = datiGrafico.some(d => d.rip !== null && d.rip !== undefined)
                  const hasKg = datiGrafico.some(d => d.kg > 0)
                  return (
                    <>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6, fontFamily: 'Syne', fontWeight: 700 }}>
                          {hasRip ? '🔁 Ripetizioni' : '⏱️ Secondi'}
                        </div>
                        <ResponsiveContainer width="100%" height={160}>
                          <LineChart data={datiGrafico} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                            <XAxis dataKey="data" tick={{ fontSize: 9, fill: 'var(--muted)' }} />
                            <YAxis tick={{ fontSize: 9, fill: 'var(--muted)' }} />
                            <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11 }} />
                            <Line type="monotone" dataKey="rip" stroke="var(--accent)" strokeWidth={2} dot={{ fill: 'var(--accent)', r: 3 }} name={hasRip ? 'Rip' : 'Sec'} connectNulls />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      {hasKg && (
                        <div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6, fontFamily: 'Syne', fontWeight: 700 }}>⚖️ Carico (kg)</div>
                          <ResponsiveContainer width="100%" height={160}>
                            <LineChart data={datiGrafico} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                              <XAxis dataKey="data" tick={{ fontSize: 9, fill: 'var(--muted)' }} />
                              <YAxis tick={{ fontSize: 9, fill: 'var(--muted)' }} />
                              {/* Il punto del grafico porta con sé la propria riga in
                                  "payload": si legge da lì. Prima si usava props.index
                                  per ripescare il dato in datiGrafico, ma quell'indice
                                  non fa parte di ciò che Recharts passa al formatter —
                                  era sempre undefined, quindi il colore dell'elastico
                                  non compariva mai e si leggeva "0 kg". */}
                              <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11 }}
                                formatter={(value, name, voce) => {
                                  const elastico = voce?.payload?.elastico
                                  if (elastico) return [elastico, '🦺 Elastico']
                                  return [`${value} kg`, 'Carico']
                                }} />
                              <Line type="monotone" dataKey="kg" stroke="var(--purple)" strokeWidth={2} dot={{ fill: 'var(--purple)', r: 3 }} name="KG" connectNulls />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            </div>
          )}

          {/* Storico ultimi risultati */}
          {progressiStorico.length > 0 && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">📋 Storico risultati</div>
                <span className="badge" style={{ background: 'var(--surface2)', color: 'var(--muted)' }}>{progressiStorico.length}</span>
              </div>
              <div style={{ padding: '0 16px' }}>
                {progressiStorico.map((t, i) => (
                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < progressiStorico.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {giorno(t.data).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                      {t.variante_nome && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{t.variante_nome}</div>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ textAlign: 'right' }}>
                        {t.ripetizioni > 0 && <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 14 }}>{t.ripetizioni} rip</div>}
                        {t.durata_secondi > 0 && <div style={{ color: 'var(--accent2)', fontWeight: 700, fontSize: 14 }}>{t.durata_secondi}s</div>}
                        {t.elastico && <div style={{ fontSize: 11, color: 'var(--accent3)' }}>🦺 {t.elastico}</div>}
                        {!t.elastico && t.peso > 0 && <div style={{ fontSize: 12, color: 'var(--accent3)', fontWeight: 600 }}>+{t.peso}kg</div>}
                        {/* Il numero della serie è l'informazione utile ora che ogni
                            serie è una riga. Le righe vecchie dell'allenamento personale
                            non ce l'hanno e tenevano il conteggio in "sets": si mostra
                            quello com'era, invece di inventare un numero di serie. */}
                        {t.serie_numero != null
                          ? <div style={{ fontSize: 10, color: 'var(--muted)' }}>Serie {t.serie_numero}</div>
                          : t.sets ? <div style={{ fontSize: 10, color: 'var(--muted)' }}>{t.sets} set insieme</div> : null}
                      </div>
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 8, fontWeight: 700,
                        background: t.fonte === 'personale' ? 'rgba(87,184,255,0.12)' : 'rgba(127,214,61,0.12)',
                        color: t.fonte === 'personale' ? 'var(--accent2)' : 'var(--accent)' }}>
                        {t.fonte === 'personale' ? 'Solo' : 'Gruppo'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        /* ── LISTA ESERCIZI MONITORATI ── */
        <>
          {tracking.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: 44, marginBottom: 14 }}>📈</div>
              <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Nessun esercizio monitorato</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.6 }}>Aggiungi esercizi dalla libreria o inizia ad allenarti</div>
              <button className="btn btn-primary" onClick={() => setMostraLibreria(true)} style={{ maxWidth: 200, margin: '0 auto' }}>+ Aggiungi esercizio</button>
            </div>
          ) : (() => {
            // Raggruppa per esercizio con record massimi
            const esUnici = [], seen = new Set(), recordMap = {}
            tracking.forEach(t => {
              if (!seen.has(t.esercizio_id)) {
                seen.add(t.esercizio_id)
                esUnici.push({ id: t.esercizio_id, nome: t.esercizi_db?.nome || '?', tipo_misura: t.esercizi_db?.tipo_misura || 'ripetizioni', varianti: [], regressioni: [] })
                recordMap[t.esercizio_id] = { maxRip: 0, maxTempo: 0, maxPeso: 0, ultima: t.data, fonte: t.fonte }
              }
              const r = recordMap[t.esercizio_id]
              if (t.ripetizioni > r.maxRip) r.maxRip = t.ripetizioni
              if (t.durata_secondi > r.maxTempo) r.maxTempo = t.durata_secondi
              if (t.peso > r.maxPeso) r.maxPeso = t.peso
            })
            return (
              <>
                <div className="card">
                  <div className="card-header">
                    <div className="card-title">🏆 Record personali</div>
                  </div>
                  <div style={{ padding: '0 16px' }}>
                    {esUnici.map((ex, i) => {
                      const r = recordMap[ex.id]
                      // Livello rapido
                      let livelloBar = 0
                      if (ex.tipo_misura !== 'tempo' && r.maxRip > 0) {
                        livelloBar = r.maxRip >= 30 ? 5 : r.maxRip >= 20 ? 4 : r.maxRip >= 12 ? 3 : r.maxRip >= 6 ? 2 : 1
                      } else if (ex.tipo_misura === 'tempo' && r.maxTempo > 0) {
                        livelloBar = r.maxTempo >= 60 ? 5 : r.maxTempo >= 45 ? 4 : r.maxTempo >= 30 ? 3 : r.maxTempo >= 15 ? 2 : 1
                      }
                      const livColore = ['','var(--purple)','var(--green)','var(--accent2)','var(--accent)','var(--accent3)'][livelloBar]
                      return (
                        <div key={ex.id} onClick={() => apriEsercizioProgressi(ex)}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < esUnici.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{ex.nome}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, display: 'flex', gap: 6, alignItems: 'center' }}>
                              <span>Ultima: {giorno(r.ultima).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}</span>
                              <span style={{ background: r.fonte === 'personale' ? 'rgba(87,184,255,0.12)' : 'rgba(127,214,61,0.12)', color: r.fonte === 'personale' ? 'var(--accent2)' : 'var(--accent)', fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8 }}>
                                {r.fonte === 'personale' ? 'Solo' : 'Gruppo'}
                              </span>
                            </div>
                            {livelloBar > 0 && (
                              <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
                                {[1,2,3,4,5].map(n => (
                                  <div key={n} style={{ width: 16, height: 3, borderRadius: 2, background: n <= livelloBar ? livColore : 'var(--surface2)' }} />
                                ))}
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign: 'right', marginRight: 10, flexShrink: 0 }}>
                            {r.maxRip > 0 && <div style={{ color: 'var(--accent)', fontWeight: 800, fontSize: 18, lineHeight: 1 }}>{r.maxRip}<span style={{ fontSize: 10, fontWeight: 400, color: 'var(--muted)', marginLeft: 2 }}>rip</span></div>}
                            {r.maxTempo > 0 && <div style={{ color: 'var(--accent2)', fontWeight: 800, fontSize: 18, lineHeight: 1 }}>{r.maxTempo}<span style={{ fontSize: 10, fontWeight: 400, color: 'var(--muted)', marginLeft: 2 }}>s</span></div>}
                            {r.maxPeso > 0 && <div style={{ fontSize: 12, color: 'var(--accent3)', fontWeight: 700 }}>+{r.maxPeso}kg</div>}
                          </div>
                          <span style={{ color: 'var(--muted)', fontSize: 16 }}>›</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </>
            )
          })()}
        </>
      )}
    </div>
  </div>
  )
}
